@let workersCount = workersDataCount();
@let currentPage = currentWorkerPage();
@let isWorkersLoaded = isLoadingStatus(workersLoadingStatus(), LoadingState.READY);
@let isWorkersLoading = isLoadingStatus(workersLoadingStatus(), LoadingState.LOADING);
@let isTableLoading = isWorkersLoading;
@let isDeleteLoading = isLoadingStatus(workerDeleteLoadingStatus(), LoadingState.LOADING);
@let workers = (workers$ | async) ?? [];
@let isEmptyState = isWorkersLoaded && workersCount === 0 && !isFiltersActive;

<section class="page-content page-viewport">
  <mon-page-toolbar
    titleTranslateKey="monitoring.menu.workers"
    [searchControl]="isEmptyState ? null : searchControl"
    searchPlaceholderTranslateKey="monitoring.workers.dashboard.search"
    [showFiltersButton]="!isEmptyState"
    [selectedFiltersCount]="isEmptyState ? 0 : selectedFiltersCount"
    [actions]="toolbarActions(isEmptyState)"
    (actionClick)="onToolbarAction($event)"
  >
    <nav
      toolbarNav
      class="page-content__nav"
    >
      @for (item of pageNavigation; track item.id) {
        <lib-button
          buttonStyle="transparent-secondary"
          [routerLink]="item.url"
          routerLinkActive="active"
          [routerLinkActiveOptions]="{ exact: true }"
        >
          {{ item.titleTranslateKey | translate }}
        </lib-button>
      }
    </nav>

    @if (!isEmptyState) {
      <ng-container toolbarFilters>
        <div class="workers__filters">
          <mon-coin-selector
            class="workers__filter"
            [controlEntity]="coinControl"
            (selectionChange)="onCoinsSelectionChange($event)"
          />

          <mon-pool-selector
            class="workers__filter"
            [controlEntity]="poolControl"
            (selectionChange)="onPoolsSelectionChange($event)"
          />

          <mon-worker-status-selector
            class="workers__filter"
            [controlEntity]="statusControl"
            (selectionChange)="onStatusesSelectionChange($event)"
          />
        </div>
      </ng-container>
    }

    @if (!isEmptyState && isFiltersActive) {
      <div
        toolbarSelectedFilters
        class="workers__selected-filters"
      >
        <lib-button
          class="chip chip--button"
          buttonStyle="danger"
          buttonSize="medium"
          (click)="clearAllFilters()"
        >
          <span>{{ 'monitoring.workers.dashboard.clear-filters' | translate }}</span>
        </lib-button>

        @for (coin of selectedCoins; track coin.id) {
          <lib-button
            class="chip chip--button"
            buttonStyle="danger"
            buttonSize="medium"
            (click)="removeFilter('coinIds', coin.id)"
          >
            <lib-svg-icon
              icon="close"
              size="16"
            />
            <span>{{ coin.name }}</span>
          </lib-button>
        }

        @for (pool of selectedPools; track pool.id) {
          <lib-button
            class="chip chip--button"
            buttonStyle="danger"
            buttonSize="medium"
            (click)="removeFilter('poolIds', pool.id)"
          >
            <lib-svg-icon
              icon="close"
              size="16"
            />
            <span>{{ pool.name }}</span>
          </lib-button>
        }

        @for (status of selectedStatuses; track status.id) {
          <lib-button
            class="chip chip--button"
            buttonStyle="danger"
            buttonSize="medium"
            (click)="removeFilter('statusIds', status.id)"
          >
            <lib-svg-icon
              icon="close"
              size="16"
            />
            <span>{{ status.name }}</span>
          </lib-button>
        }
      </div>
    }
  </mon-page-toolbar>

  @if (isEmptyState) {
    <div class="entity-addition">
      <div class="entity-addition__inner">
        <lib-svg-icon
          icon="share-circle"
          size="44"
        />
        <div>{{ 'monitoring.workers.no-workers-available' | translate }}</div>
        @if (isManualWorkersPage && canManageWorkers()) {
          <lib-button
            buttonStyle="primary"
            buttonSize="large"
            (click)="openWorkerModal(WorkerAction.Create)"
          >
            <span>{{ 'monitoring.workers.dashboard.add-worker' | translate }}</span>
          </lib-button>
        }
      </div>
    </div>
  } @else {
    <section class="page-content page-table page-table-scroll">
      <div class="page-table-scroll__content">
        <lib-table
          class="page-table-scroll__table"
          [class.workers-table--ready]="isWorkersLoaded"
          [columns]="columns()"
          [dataSource]="workers"
          [cellTemplates]="{
            worker: workerTpl,
            client: clientTpl,
            pool: poolTpl,
            coin: coinTpl,
            device: deviceTpl,
            status: statusTpl,
            hashrate: hashrateTpl,
            actions: actionsTpl,
          }"
          [loaded]="isWorkersLoaded"
          [loading]="isTableLoading"
        />
      </div>

      @if (!(workers.length === 0 && currentPage === 1)) {
        <lib-pagination
          [loading]="isTableLoading"
          [totalCount]="workersCount"
          [pageIndex]="currentPage"
          [pageSize]="100"
          (paginationChange)="onPageChange($event)"
        />
      }
    </section>
  }

  <ng-template
    #workerTpl
    let-row="data"
  >
    <div class="workers__cell">
      <div class="workers__cell-name">{{ typedRow(row).name || '—' }}</div>
    </div>
  </ng-template>

  <ng-template
    #clientTpl
    let-row="data"
  >
    <div class="workers__cell">
      <div
        class="workers__cell-client"
        [class.workers__cell--color-cell]="!typedRow(row).clientName"
        [class.workers__cell--color-cell--orange]="!typedRow(row).clientName"
      >
        {{ typedRow(row).clientName || ('monitoring.workers.no-client' | translate) }}
      </div>
    </div>
  </ng-template>

  <ng-template
    #poolTpl
    let-row="data"
  >
    <div class="workers__cell">
      <div class="workers__cell--flex-row">
        @if (typedRow(row).poolName) {
          <lib-icon-button
            icon="link"
            [size]="16"
          />
          <span>{{ typedRow(row).poolName }}</span>
        } @else {
          <span>&mdash;</span>
        }
      </div>
    </div>
  </ng-template>

  <ng-template
    #coinTpl
    let-row="data"
  >
    <div class="workers__cell">
      <div class="workers__cell--flex-row workers__cell-coin">
        @if (typedRow(row).coin.ticker) {
          <lib-special-icon
            [icon]="specialIconType(typedRow(row).coin.ticker)"
            [size]="32"
          />
          <span>{{ typedRow(row).coin.ticker }}</span>
        } @else {
          <span>&mdash;</span>
        }
      </div>
    </div>
  </ng-template>

  <ng-template
    #deviceTpl
    let-row="data"
  >
    <div class="workers__cell workers__cell--flex-row">
      <div
        class="workers__cell--flex-row workers__cell--color-cell workers__cell--color-cell--gray"
      >
        @if (typedRow(row).equipmentQuantity) {
          <lib-icon-button
            icon="pick"
            [size]="16"
          />
          <span>{{ typedRow(row).equipmentQuantity }}</span>
        } @else {
          <span>&mdash;</span>
        }
      </div>

      <div
        class="workers__cell--flex-row workers__cell--color-cell workers__cell--color-cell--orange"
      >
        @if (typedRow(row).equipmentUnderRepairQuantity) {
          <lib-icon-button
            icon="screwdriver"
            [size]="16"
          />
          <span>{{ typedRow(row).equipmentUnderRepairQuantity }}</span>
        } @else {
          <span>&mdash;</span>
        }
      </div>
    </div>
  </ng-template>

  <ng-template
    #statusTpl
    let-row="data"
  >
    <div class="workers__cell">
      <div
        [class]="
          'workers__cell--flex-row workers__cell--color-cell workers__cell--color-cell--' +
          classByStatus(typedRow(row).status.name)
        "
      >
        {{
          'monitoring.workers.status.' + (typedRow(row).status.code || 'unrecognized') | translate
        }}
      </div>
    </div>
  </ng-template>

  <ng-template
    #hashrateTpl
    let-row="data"
  >
    @let worker = typedRow(row);

    <div class="workers__cell workers__cell--flex-row">
      @if (worker.totalHashrate !== undefined) {
        <span>{{ worker.totalHashrate | hashrate: 'onlyDigit' }}</span>
        <span class="workers__cell-hashrate-speed">
          {{ worker.totalHashrate | hashrate: 'onlyLabel' }}
        </span>
      } @else {
        <span>&mdash;</span>
      }
    </div>
  </ng-template>

  <ng-template
    #actionsTpl
    let-row="data"
  >
    @let data = typedRow(row);
    <div class="workers__actions">
      @if (canManageWorkers()) {
        <lib-icon-button
          icon="pen"
          [disabled]="!data.isEditable || isDeleteLoading"
          [title]="'monitoring.workers.actions.edit' | translate"
          (click)="openWorkerModal(WorkerAction.Edit, data)"
        />

        <lib-icon-button
          icon="trash"
          [disabled]="!data.isEditable || isDeleteLoading"
          [title]="'monitoring.workers.actions.delete' | translate"
          (click)="confirmDeleteWorker(data)"
        />
      }
    </div>
  </ng-template>
</section>
